import { isInvidiousPlaylistId, type Playlist, type PlaylistEntry } from '../model/playlist.ts';
import type { PlaylistListing } from './entries.ts';

/**
 * §8's Playlists row, **writing** half: the five mutations, the bodies they take, and the six rules
 * that keep a mutation from failing in a way that reads as an authentication problem.
 *
 * In core rather than in the screen for `channel/tabs.ts`'s reason, with one addition that applies
 * only here: **a write that half-happens cannot be undone by re-rendering.** A wrong assumption in a
 * `GET` is a wrong pixel; a wrong assumption in `PATCH /api/v1/auth/playlists/{id}` is a playlist whose
 * title was replaced by an empty string. So each rule below carries the measurement it came from.
 *
 * Everything was measured against `invidious.example.com` on 2026-07-29, ~200 real writes
 * (API reference §7i). The three findings that changed the design:
 *
 * 1. **`PATCH` requires `title` on every call.** Omitting it is `403 {"error":"Missing hash key:
 *    \"title\""}` — so "change the description" and "change the privacy" both have to re-send the
 *    title they are not changing. See {@link updatePlaylistBody}.
 * 2. **The title is capped at 150 *bytes*, and the cap cuts UTF-8 mid-character.** An over-long ASCII
 *    title is silently truncated; an over-long title with any multi-byte character is refused with a
 *    leaked *Postgres* error at `403`. See {@link clampPlaylistTitle}.
 * 3. **`privacy` is write-only.** No read returns it, so a playlist's privacy cannot be displayed —
 *    only `isListed`, which cannot separate `unlisted` from `private`. See {@link playlistVisibility}.
 */

/* ------------------------------------------------------------------------------------------------
 * Paths
 * ---------------------------------------------------------------------------------------------- */

export const AUTH_PLAYLISTS_PATH = '/api/v1/auth/playlists';

/**
 * The authenticated read of one playlist. Needed for a **private** playlist — the public endpoint
 * answers `404 {"error":"Playlist does not exist."}` for one, and **ignores the bearer token**, so it
 * cannot be talked into serving one: `GET /api/v1/playlists/{privateId}` is `404` with the credential
 * attached exactly as it is without (measured, both 36 bytes).
 *
 * It is a **superset** of the public endpoint rather than a private-only variant: the same path with a
 * YouTube playlist id answers `200` with the mirrored playlist (260 KB for a 500-entry one). So a
 * signed-in reader could route everything through it — {@link authPlaylistReadPreferred} says why this
 * build does not.
 */
export function authPlaylistPath(playlistId: string): string {
  return `${AUTH_PLAYLISTS_PATH}/${encodeURIComponent(playlistId)}`;
}

export function authPlaylistVideosPath(playlistId: string): string {
  return `${authPlaylistPath(playlistId)}/videos`;
}

/**
 * **Keyed on `indexId`, never on the numeric `index`**, and the wrong key fails in two different ways
 * that are both easy to misread:
 *
 * ```
 * DELETE …/videos/1                 → 404 {"error":"Playlist does not contain index"}   ← index 1 exists
 * DELETE …/videos/0000000000000000  → 404 {"error":"Playlist does not contain index"}   ← no such key
 * DELETE …/videos/jNQXAC9IVRw       → 403 {"error":"Invalid Int64: \"jNQXAC9IVRw\""}    ← a video id
 * DELETE …/videos/7096D5A5DFF0531A  → 204
 * ```
 *
 * So the wrong *value* of the right kind is a `404` whose message reads as "that position is gone",
 * and the wrong *kind* is a `403` that reads as an auth failure.
 *
 * **The key is an Int64 written in hex, which is more forgiving than it looks and narrower in one way
 * that matters.** Measured one fresh entry per case, so that nothing under test had already been
 * removed:
 *
 * ```
 * 74F537B39C70C52D → 74f537b39c70c52d    204   lowercased  — case-insensitive
 * CA43C315FBBFB8B  → CA43C315fbbfb8b     204   mixed case
 * 6376647A75BCDFAA → 006376647A75BCDFAA  204   leading zeros
 * 4F1CF5B7AD4EFFB1 → 0x4F1CF5B7AD4EFFB1  403   Invalid Int64
 * 6CB0EC7D52DE5C7A → 7832019775002664058 403   Invalid Int64 — the same number, in decimal
 * ```
 *
 * Which pins it as `to_i64(16)`. The consequence for this app is not the tolerance but the **width**:
 * `CA43C315FBBFB8B` is **15** digits, not 16, because the number is rendered without leading zeros. The
 * API reference recorded "16 uppercase hex digits" from a sample of three (0g) — so a validator built
 * to that shape would refuse about one key in sixteen. There is nothing to validate: pass `indexId`
 * through exactly as it arrived.
 */
export function authPlaylistVideoPath(playlistId: string, indexId: string): string {
  return `${authPlaylistVideosPath(playlistId)}/${encodeURIComponent(indexId)}`;
}

/**
 * Which read to use for this playlist id, given a session.
 *
 * The authenticated read serves everything the public one does, so "always use it when signed in"
 * would work — and would attach a credential to every public playlist a signed-in reader opens, for no
 * benefit. R9's least-privilege reading applies to *sending* the token as much as to storing it, so
 * the credential goes out only where it buys something: an `IVPL…` id, which is the only kind that can
 * be private.
 *
 * A non-`IVPL…` id is a mirror of a YouTube playlist and is public by construction.
 */
export function authPlaylistReadPreferred(playlistId: string, signedIn: boolean): boolean {
  return signedIn && isInvidiousPlaylistId(playlistId);
}

/* ------------------------------------------------------------------------------------------------
 * Privacy
 * ---------------------------------------------------------------------------------------------- */

/** The `privacy` enum, verbatim. Sent lowercase; the instance parses case-insensitively. */
export type PlaylistPrivacy = 'public' | 'unlisted' | 'private';

/** Order is the order a chooser should offer them in: most open first, like YouTube's own. */
export const PLAYLIST_PRIVACIES: readonly PlaylistPrivacy[] = ['public', 'unlisted', 'private'];

export function asPlaylistPrivacy(input: string): PlaylistPrivacy | null {
  const lower = input.trim().toLowerCase();
  return PLAYLIST_PRIVACIES.find((privacy) => privacy === lower) ?? null;
}

/**
 * What a read can honestly say about a playlist's privacy — which is **less than the three values a
 * write accepts**, and that asymmetry is this slice's most awkward finding.
 *
 * No response carries `privacy`. Not the list, not the authenticated read of one playlist, not the
 * `201` from creating it. The only related field is `isListed`, and it is a boolean over three states:
 *
 * ```
 * created with privacy=public    → isListed true    public GET 200
 * created with privacy=unlisted  → isListed false   public GET 200
 * created with privacy=private   → isListed false   public GET 404
 * ```
 *
 * So `isListed: false` means "unlisted **or** private" and nothing can narrow it from the payload. The
 * two are separable — an unauthenticated `GET /api/v1/playlists/{id}` is `200` for unlisted and `404`
 * for private — but that is a second request per playlist to learn something the reader can also simply
 * re-set, so this build does not make it and says so instead.
 *
 * The gap is *closed for the case that matters*: whenever this session created the playlist or set its
 * privacy, the value is known exactly, because we are the one who sent it. See
 * {@link PlaylistVisibility.privacy}.
 */
export type PlaylistListedState = 'listed' | 'not-listed';

export interface PlaylistVisibility {
  readonly listed: PlaylistListedState;
  /**
   * The exact value, or `null` when the API will not say. Non-null only when this session set it —
   * a create or a `PATCH` — which is what keeps a privacy control from having to guess an initial value
   * in the flow where the reader just chose one.
   */
  readonly privacy: PlaylistPrivacy | null;
}

export function playlistVisibility(
  playlist: Pick<Playlist, 'isListed'>,
  known: PlaylistPrivacy | null = null,
): PlaylistVisibility {
  return { listed: playlist.isListed ? 'listed' : 'not-listed', privacy: known };
}

/** Whether a link to this playlist works for somebody who is not signed in. The `listed` half is exact. */
export function playlistIsReachableByLink(visibility: PlaylistVisibility): boolean | null {
  if (visibility.privacy !== null) return visibility.privacy !== 'private';
  return visibility.listed === 'listed' ? true : null;
}

/* ------------------------------------------------------------------------------------------------
 * The title, and the 150-byte cap that refuses rather than truncates
 * ---------------------------------------------------------------------------------------------- */

/**
 * Postgres truncates the column at **150 bytes** — not characters — and the truncation happens *before*
 * the string is validated, so cutting inside a multi-byte character makes the whole write fail:
 *
 * ```
 * 150 × "a"   (150 B)  → 201, stored 150 characters
 * 151 × "a"   (151 B)  → 201, stored 150 characters          ← silently truncated
 * 151 × "ä"   (302 B)  → 201, stored  75 characters          ← 150 B happens to be a boundary
 * 148 × "a" + "🎬" (152 B) → 403 {"error":"invalid byte sequence for encoding \"UTF8\": 0xf0 0x9f"}
 *  38 × "🎬"  (152 B)  → 403 (the same)
 *  37 × "🎬"  (148 B)  → 201
 * ```
 *
 * So an over-long **ASCII** title is quietly shortened and an over-long title with any non-ASCII
 * character is a hard failure — at `403`, wearing the status this project has already measured four
 * other meanings for, with a leaked Postgres message that says nothing about titles. That is not a
 * theoretical edge: `ä` is two bytes, so a German title of 76 characters reaches the cap, and the same
 * `PATCH` that renames a playlist is where it lands.
 *
 * Hence a client-side cap, cutting on a **character** boundary, applied to every write.
 */
export const PLAYLIST_TITLE_MAX_BYTES = 150;

/** UTF-8 length without `TextEncoder` — R1 leaves core no DOM and no Node globals. */
export function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (const character of text) {
    const code = character.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

export interface ClampedTitle {
  readonly title: string;
  /** Whether anything was dropped — so the writer can say so rather than let it happen silently. */
  readonly truncated: boolean;
}

/**
 * The longest prefix of `title` that fits in {@link PLAYLIST_TITLE_MAX_BYTES}, cut between characters.
 *
 * Iterating by code point rather than by UTF-16 unit is the whole point: a surrogate pair split in half
 * is exactly the input that produces the `403` above, and `title.slice(0, n)` in JavaScript splits one.
 */
export function clampPlaylistTitle(title: string): ClampedTitle {
  let bytes = 0;
  let kept = '';
  for (const character of title) {
    const width = utf8ByteLength(character);
    if (bytes + width > PLAYLIST_TITLE_MAX_BYTES) return { title: kept, truncated: true };
    bytes += width;
    kept += character;
  }
  return { title: kept, truncated: false };
}

export type PlaylistTitleProblem =
  /** Nothing but whitespace. The API **accepts** this at `201` — see below. */
  | 'empty';

export type PlaylistTitleCheck =
  | { readonly ok: true; readonly title: string; readonly truncated: boolean }
  | { readonly ok: false; readonly problem: PlaylistTitleProblem };

/**
 * The title as it should go on the wire, or why it cannot.
 *
 * **Trimming and the empty check are ours because the API does neither.** `{"title":""}` answers `201`
 * and `{"title":"   "}` stores three spaces, so an untitled playlist is a state the instance will
 * happily create and every list then has a row with nothing to click. (Only a *missing* `title` key is
 * refused, at `400 {"error":"Invalid title."}` — which is a different thing and unreachable from a form.)
 *
 * `<` and `>` are stripped by the instance and nothing else is: `&`, quotes, tabs, CR/LF and bidi
 * controls all round-trip verbatim. Not corrected here — a title is rendered as text (R8 keeps HTML out
 * of the model entirely), so the only consequence is that the stored title may differ from what was
 * typed, and inventing a second sanitiser would be the more surprising behaviour.
 */
export function checkPlaylistTitle(input: string): PlaylistTitleCheck {
  const trimmed = input.trim();
  if (trimmed === '') return { ok: false, problem: 'empty' };
  const clamped = clampPlaylistTitle(trimmed);
  // Re-trim: the clamp can leave a trailing space where it cut.
  const title = clamped.title.trimEnd();
  return title === ''
    ? { ok: false, problem: 'empty' }
    : { ok: true, title, truncated: clamped.truncated };
}

/* ------------------------------------------------------------------------------------------------
 * Request bodies
 * ---------------------------------------------------------------------------------------------- */

/**
 * Every write takes **JSON**, and that is measured rather than assumed: the same create sent as
 * `application/x-www-form-urlencoded` answers `400 {"error":"Invalid title."}`, i.e. the fields are
 * simply not seen. There is no form-encoded fallback to be tolerant with.
 */
export interface CreatePlaylistInput {
  readonly title: string;
  readonly privacy: PlaylistPrivacy;
}

/**
 * `{title, privacy}` — and **both are mandatory**, with a different refusal each:
 *
 * ```
 * POST {privacy:"private"}                → 400 {"error":"Invalid title."}
 * POST {title:"x"}                        → 400 {"error":"Invalid privacy setting."}
 * POST {title:"x", privacy:"bogus"}       → 403 {"error":"Unknown enum PlaylistPrivacy value: bogus"}
 * POST  (no body at all)                  → 400 {"error":"Invalid title."}
 * ```
 *
 * The third is why `privacy` is a union rather than a string: an unrecognised value is not ignored and
 * not a `400`, it is a *fifth* `403` on a path where four other meanings already live.
 */
export function createPlaylistBody(input: CreatePlaylistInput): Readonly<Record<string, string>> {
  return { title: input.title, privacy: input.privacy };
}

export interface UpdatePlaylistInput {
  /**
   * **Mandatory, even when it is not what is being changed.** This is the slice's central trap:
   *
   * ```
   * PATCH {title:"new"}                                     → 204
   * PATCH {description:"new"}                               → 403 {"error":"Missing hash key: \"title\""}
   * PATCH {privacy:"private"}                               → 403 {"error":"Missing hash key: \"title\""}
   * PATCH {}                                                → 403 {"error":"Missing hash key: \"title\""}
   * PATCH {title:"new", description:"new", privacy:"private"} → 204
   * ```
   *
   * So there is no such thing as a partial update: a caller that wants to set the privacy must know
   * the current title and send it back. And the failure is a `403` with a leaked Crystal `KeyError`,
   * which — before 3b's structural fix — would have been read as `urls-expired` and then rendered as
   * "the session has expired" to a reader whose session was fine.
   *
   * The type makes it unrepresentable rather than documented: `title` is required here.
   */
  readonly title: string;
  /** Omitted leaves the stored description **unchanged**; `''` clears it. Both measured. */
  readonly description?: string;
  /** Omitted leaves the stored privacy **unchanged**. Measured across all three values. */
  readonly privacy?: PlaylistPrivacy;
}

/**
 * The `PATCH` body. Omitted fields are preserved by the instance, verified rather than assumed:
 * `{title, description, privacy:'unlisted'}` then `{title}` alone left the description and `isListed`
 * exactly as they were.
 *
 * There is **no length cap on `description`** — 6 000 characters stored and returned in full, where the
 * title truncates at 150 bytes. And `descriptionHtml` for a playlist of ours is the plain text
 * HTML-escaped (`&lt;b&gt;`, and `&amp;` becomes `&amp;amp;`) with no linkification, which is why
 * `parsePlaylist` reading nodes from `description` is right for a native playlist as well as for a
 * mirrored one.
 */
export function updatePlaylistBody(input: UpdatePlaylistInput): Readonly<Record<string, string>> {
  const body: Record<string, string> = { title: input.title };
  if (input.description !== undefined) body.description = input.description;
  if (input.privacy !== undefined) body.privacy = input.privacy;
  return body;
}

/**
 * `{videoId}` — and the response is the **whole entry row**, `indexId` and `index` included:
 *
 * ```
 * POST …/videos {"videoId":"dQw4w9WgXcQ"}  → 201
 * {"type":"video","title":"Rick Astley — …","videoId":"dQw4w9WgXcQ", …,
 *  "index":0,"indexId":"47F19791B29A99E6","lengthSeconds":213,"liveNow":false}
 * ```
 *
 * So an add needs **no follow-up read** to know where the row landed or how to remove it again — the
 * `201` body parses with `parsePlaylistEntry` unchanged. (There is a `Location` header too, pointing at
 * the removal URL; {@link CREATED_PLAYLIST_LOCATION_IS_UNREADABLE} says why it cannot be used.)
 *
 * Two refusals share `404` and only the body separates them:
 * `{"error":"Video unavailable"}` for a video id YouTube will not serve, and
 * `{"error":"Playlist does not exist."}` for a playlist that is gone. A missing `videoId` key is
 * `403 {"error":"Missing hash key: \"videoId\""}`.
 */
export function addPlaylistVideoBody(videoId: string): Readonly<Record<string, string>> {
  return { videoId };
}

/**
 * **The `201`'s `Location` header exists on the wire and is unreadable from a browser**, so the
 * `playlistId` must come out of the body.
 *
 * ```
 * POST /api/v1/auth/playlists → 201
 *   location:                      https://…/api/v1/auth/playlists/IVPL4c_E6P2NUZT5uDVKCz1czgPgJj8ElwN
 *   access-control-expose-headers: Content-Length, Content-Range, Accept-Ranges
 *   body:                          {"title":"…","playlistId":"IVPL4c_E6P2NUZT5uDVKCz1czgPgJj8ElwN"}
 * ```
 *
 * `Location` is not a CORS-safelisted response header and the instance does not expose it, so
 * `response.headers.get('location')` is `null` in a browser however plainly `curl` shows it. Invidious's
 * documentation says to read the header; this repo's API reference said the header does not exist (0g,
 * measured with `curl` — and wrong in the other direction). Both readings lead somewhere that does not
 * work, and the body was the answer all along.
 *
 * A constant rather than a comment because it is the kind of thing a later slice "fixes" by reading the
 * header: exposing it would need a change to the *instance's* CORS configuration.
 */
export const CREATED_PLAYLIST_LOCATION_IS_UNREADABLE = true;

/* ------------------------------------------------------------------------------------------------
 * What is editable
 * ---------------------------------------------------------------------------------------------- */

/**
 * Whether the mutation endpoints apply to this playlist **for this reader**.
 *
 * A YouTube playlist is a read-only mirror and the instance says so with the same `404` a deleted
 * playlist gets — `PATCH` and `DELETE` on `PL6Lt9p1lIRZ…` both answer
 * `404 {"error":"Playlist does not exist."}`. So the UI must not offer the control rather than offer
 * one that fails: the failure is indistinguishable from "somebody deleted this a second ago", and a
 * reader would reasonably conclude the app is broken.
 *
 * An `IVPL…` id is not enough on its own, which is the second half of the same rule. Every account on
 * an instance mints ids of that shape and the unauthenticated endpoint serves a listed one, so a
 * signed-in reader opening somebody else's playlist was offered a remove, a rename and a manage mode.
 * All four write routes open with `playlist.author != user.email` and refuse — `403 Invalid user` for
 * a listed playlist, `404 Playlist does not exist.` for a private one (API reference §7i). `author`
 * on a native playlist **is** that `user.email`, and `viewerUsername` is the same name as the grant
 * stated it (`AuthGrant` — the API has no "who am I" endpoint, so the callback is the only place it
 * is said at all).
 *
 * Where one of the two names is missing there is nothing to compare, and the answer is **yes**: a
 * reader kept out of their own playlist has lost the feature, while one offered a control that
 * refuses has lost a click.
 */
export function playlistEditable(
  playlist: Pick<Playlist, 'invidiousOwned' | 'author'>,
  viewerUsername: string | null,
): boolean {
  if (!playlist.invidiousOwned) return false;
  if (viewerUsername === null || viewerUsername === '' || playlist.author === '') return true;
  return playlist.author === viewerUsername;
}

/**
 * Whether *this row* can be removed. Per entry rather than per playlist, because §6 types `indexId`
 * nullable for a measured reason: a YouTube playlist's rows carry no `indexId` at all, and there is no
 * other key removal accepts.
 */
export function playlistEntryRemovable(entry: Pick<PlaylistEntry, 'indexId'>): boolean {
  return entry.indexId !== null;
}

/** There is no reorder endpoint. A constant so a caller can state the gap rather than look for one. */
export const PLAYLIST_REORDER_SUPPORTED = false;

/* ------------------------------------------------------------------------------------------------
 * Removal, locally — because `index` is a position and the server renumbers
 * ---------------------------------------------------------------------------------------------- */

/**
 * The listing with those entries gone **and the later positions renumbered**, which is what the server
 * does and is why this cannot be a filter.
 *
 * ```
 * before                     0:30EBDA…  1:2BB75B…  2:77B971…  3:30F7E8…  4:645836…   videoCount 120
 * DELETE …/videos/2BB75B…    → 204
 * after                      0:30EBDA…  1:77B971…  2:30F7E8…  3:645836…  4:1D94E4…   videoCount 119
 * ```
 *
 * **So `index` is a position, not an identity** — and 2e's `foldPlaylistPages` keys on it. Dropping the
 * removed row without renumbering leaves every later entry's number one too high, and the numbers are
 * drawn on screen; keeping the fold and re-fetching instead means the row stays visible for as long as
 * the round trip takes, which reads as "Remove did nothing" and invites a second click — whose `404`
 * is `Playlist does not contain index`, a message about a position that plainly exists.
 *
 * Applying the server's own transformation locally makes the optimistic state and the refetched state
 * the same list. An `indexId` that is not present is a no-op, so applying this to a page that has
 * already been refetched is harmless — which is what lets the pending set outlive the invalidation
 * without a race.
 *
 * Each kept entry loses one position **per removal below it** rather than being numbered by where it
 * now sits in the array. The two are the same list when the listing starts at index 0, and only one
 * of them is right for a listing that does not: a backwards read holds entries 450..574, and
 * numbering those from zero would redraw the whole panel as if the reader were at the start.
 */
export function withoutPlaylistEntries(
  listing: PlaylistListing,
  removedIndexIds: readonly string[],
): PlaylistListing {
  if (removedIndexIds.length === 0) return listing;
  const removed = new Set(removedIndexIds);

  let dropped = 0;
  const entries = listing.entries.flatMap((entry) => {
    if (entry.indexId !== null && removed.has(entry.indexId)) {
      dropped += 1;
      return [];
    }
    return [{ ...entry, index: entry.index - dropped }];
  });
  if (dropped === 0) return listing;

  return {
    ...listing,
    entries,
    // Closing the holes the removals left cannot open a new one, so this only ever restates what the
    // listing already was — and it is what the completeness proof behind "Load more" reads.
    gaplessFromZero: entries.every((entry, position) => entry.index === position),
  };
}

/** The header's count after `n` local removals. Never below zero, and `null` stays `null`. */
export function playlistCountAfterRemovals(videoCount: number | null, removed: number): number | null {
  if (videoCount === null) return null;
  return Math.max(0, videoCount - removed);
}

/**
 * How many of the pending removals are still present in the pages held — which is what may be
 * subtracted from `videoCount`, and the number {@link playlistCountAfterRemovals} wants.
 *
 * Counting the *set* instead would double-decrement after a refetch: the row is gone from the fresh
 * pages **and** still in the pending list, so a naive `videoCount - removed.length` shows one fewer
 * video than the playlist has for as long as the page stays open.
 */
export function appliedPlaylistRemovals(
  pages: readonly Pick<Playlist, 'entries'>[] | null,
  removedIndexIds: readonly string[],
): number {
  if (pages === null || removedIndexIds.length === 0) return 0;
  const present = new Set<string>();
  for (const page of pages) {
    for (const entry of page.entries) if (entry.indexId !== null) present.add(entry.indexId);
  }
  return removedIndexIds.filter((indexId) => present.has(indexId)).length;
}

/* ------------------------------------------------------------------------------------------------
 * The list endpoint, and what it costs
 * ---------------------------------------------------------------------------------------------- */

/**
 * `GET /api/v1/auth/playlists` is **not a list of summaries** — it embeds the first 100 entries of
 * every playlist, and there is no way to ask it not to.
 *
 * ```
 * 6 playlists, one of them with 120 entries   → 130 625 bytes
 *   "Visious fixture — indexId"   videoCount 3    videos 3      ~4 129 B
 *   "vs-3c privacy unlisted"      videoCount 0    videos 0        ~302 B
 *   "vs-3c paging"                videoCount 120  videos 100  ~125 166 B      ← capped at 100
 *
 * ?fields=title,playlistId   → byte-identical to the plain request
 * ?page=2 · ?max_results=1   → byte-identical to the plain request
 * ```
 *
 * So the cost is roughly **1.2 KB per entry across every playlist you own**, paid in one response,
 * with `?fields=` ignored (as it is on `/api/v1/videos` and `/api/v1/channels`) and no paging to
 * bound it. Ten playlists of a hundred videos is on the order of a megabyte to draw ten titles.
 *
 * This is 3b's avatar problem inverted — there, too little data forced N requests; here, far too much
 * arrives in one — and the mitigation is the same shape: do not fetch it until something needs it.
 * Concretely, the add-to-playlist menu on the Watch page fetches this **when it is opened**, never on
 * mount, because a page that costs a megabyte to render a button nobody pressed is the defect.
 *
 * The embedded entries do buy one thing, so the cost is not pure waste: membership. See
 * {@link playlistsContaining}.
 */
export const OWNED_PLAYLIST_LIST_EMBEDS_ENTRIES = 100;

/**
 * Which of these playlists already contain this video, and which cannot say.
 *
 * The list endpoint embeds the first {@link OWNED_PLAYLIST_LIST_EMBEDS_ENTRIES} entries, so membership
 * is **decidable for a playlist with no more than that and unknown for a longer one** — a playlist of
 * 340 videos may hold this one at position 250, and nothing in the response says. Reported as three
 * states rather than two, because "not in this playlist" and "cannot tell" lead to different words on
 * a menu row, and rendering the second as the first is how a reader adds a duplicate.
 *
 * Duplicates are worth avoiding rather than just untidy: the endpoint **allows** them. The same
 * `videoId` posted twice answers `201` twice and yields two rows with different `indexId`s, so nothing
 * upstream will refuse a second add.
 */
export type PlaylistMembership = 'present' | 'absent' | 'unknown';

export function playlistMembership(playlist: Playlist, videoId: string): PlaylistMembership {
  if (playlist.entries.some((entry) => entry.video.videoId === videoId)) return 'present';
  const known = playlist.entries.length;
  const total = playlist.videoCount;
  // Complete when the embedded rows account for the whole playlist. The cap is what makes this
  // conditional: a playlist longer than the cap can hold the video past the window we were given.
  if (total !== null && known >= total) return 'absent';
  if (known < OWNED_PLAYLIST_LIST_EMBEDS_ENTRIES) return 'absent';
  return 'unknown';
}

export function playlistsContaining(
  playlists: readonly Playlist[],
  videoId: string,
): ReadonlyMap<string, PlaylistMembership> {
  return new Map(playlists.map((p) => [p.playlistId, playlistMembership(p, videoId)]));
}

/**
 * A statement the playlists page has to make — 2b's `PlaylistNote` shape, for the owned list.
 *
 * Each is conditional on its measurement still holding, so it disappears if upstream changes rather
 * than becoming a stale apology.
 */
export type OwnedPlaylistsNote =
  /**
   * The response embedded entries, which is what it cost. `bytes` is what actually arrived, so the
   * statement is about this reader's account rather than about the endpoint in general.
   */
  | { readonly id: 'embeds-entries'; readonly playlists: number; readonly entries: number }
  /** R4 — records recognised and unusable. */
  | { readonly id: 'dropped'; readonly count: number }
  /** {@link playlistVisibility} — the API will not report privacy. Only shown when a row needs it. */
  | { readonly id: 'privacy-unreadable'; readonly count: number };

export function ownedPlaylistsNotes(
  playlists: readonly Playlist[],
  droppedCount: number,
): readonly OwnedPlaylistsNote[] {
  const notes: OwnedPlaylistsNote[] = [];

  const entries = playlists.reduce((sum, playlist) => sum + playlist.entries.length, 0);
  if (entries > 0) notes.push({ id: 'embeds-entries', playlists: playlists.length, entries });

  if (droppedCount > 0) notes.push({ id: 'dropped', count: droppedCount });

  const unreadable = playlists.filter((playlist) => !playlist.isListed).length;
  if (unreadable > 0) notes.push({ id: 'privacy-unreadable', count: unreadable });

  return notes;
}
